import * as React from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
/**
 * Combined login + register on one page (toggle). Kept dense per the design
 * brief: small type, JetBrains-numeric where relevant, no marketing chrome.
 */
export default function Login() {
  const { login, register, user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/app";
  const [mode, setMode] = React.useState("login");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [name, setName] = React.useState("");
  const [err, setErr] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (user) nav(next, { replace: true });
  }, [user, nav, next]);

  async function submit(e) {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (mode === "login") {
        await login(email.trim(), password);
      } else {
        if (!name.trim()) throw { response: { data: { detail: { message: "Name is required" }}}};
        if (password.length < 12) throw { response: { data: { detail: { message: "Password must be at least 12 characters" }}}};
        await register(email.trim(), password, name.trim());
      }
      nav(next, { replace: true });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2?.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Left rail: dense marketing-lite */}
      <div className="hidden lg:flex flex-col justify-between p-10 bg-card border-r border-brand-rule">
        <div className="flex items-center gap-2">
          <div className="h-6 w-6 rounded-md bg-brand-violet flex items-center justify-center text-white font-bold text-[13px]">F</div>
          <div className="font-bold tracking-tight text-[15px] text-brand-ink">Fable Ops</div>
        </div>
        <div className="max-w-md">
          <div className="micro-label">Operations layer</div>
          <h1 className="mt-2 text-[28px] font-bold tracking-tight text-brand-ink leading-tight">
            Run the whole business, not just the schedule.
          </h1>
          <p className="mt-3 text-[13px] text-brand-muted">
            Bookings. Comms. Boards. Rotas. HR. Pay. Books.
            One record system for multi-site operators.
          </p>
          <div className="mt-6 space-y-1.5">
            {[
              ["Seats", "up to 25 in trial"],
              ["Modules", "7 pluggable"],
              ["Realtime", "SSE, per-company"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center gap-3 text-[12px]">
                <span className="micro-label w-16">{k}</span>
                <span className="mono-num text-brand-ink">{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="text-[11px] text-brand-faint">Phase 1 — the spine. Modules ship next.</div>
      </div>

      {/* Right rail: form */}
      <div className="flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="micro-label">{mode === "login" ? "Sign in" : "Create account"}</div>
          <h2 className="mt-1 text-[22px] font-bold tracking-tight text-brand-ink">
            {mode === "login" ? "Welcome back" : "Get started"}
          </h2>
          <p className="mt-1 text-[12px] text-brand-muted">
            {mode === "login" ? "Sign in with your email and password." : "One account, many companies."}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-3" data-testid={`${mode}-form`}>
            {mode === "register" && (
              <div>
                <label className="micro-label">Name</label>
                <input className="field-input mt-1" value={name} onChange={(e)=>setName(e.target.value)}
                       autoComplete="name" data-testid="register-name" />
              </div>
            )}
            <div>
              <label className="micro-label">Email</label>
              <input type="email" required className="field-input mt-1" value={email}
                     onChange={(e)=>setEmail(e.target.value)} autoComplete="email"
                     data-testid={`${mode}-email`} />
            </div>
            <div>
              <label className="micro-label">Password</label>
              <input type="password" required minLength={mode === "register" ? 12 : 0}
                     className="field-input mt-1" value={password}
                     onChange={(e)=>setPassword(e.target.value)}
                     autoComplete={mode === "login" ? "current-password" : "new-password"}
                     data-testid={`${mode}-password`} />
              {mode === "register" && (
                <div className="mt-1 text-[10.5px] text-brand-muted">Min. <span className="mono-num">12</span> characters. Not your name or email.</div>
              )}
              {mode === "login" && (
                <div className="mt-1 text-right">
                  <Link to="/forgot-password"
                        className="text-[11px] text-brand-violet font-medium hover:underline"
                        data-testid="login-forgot-link">
                    Forgot password?
                  </Link>
                </div>
              )}
            </div>

            {err && (
              <div className="text-[12px] text-brand-coral" data-testid={`${mode}-error`}>{err}</div>
            )}

            <button type="submit" className="btn-violet w-full" disabled={busy}
                    data-testid={`${mode}-submit`}>
              {busy ? "…" : (mode === "login" ? "Sign in" : "Create account")}
            </button>
          </form>

          <div className="mt-4 text-[12px] text-brand-muted">
            {mode === "login" ? (
              <>New here? <button type="button" onClick={() => setMode("register")}
                                  className="text-brand-violet font-medium hover:underline"
                                  data-testid="switch-to-register">Create an account</button></>
            ) : (
              <>Have an account? <button type="button" onClick={() => setMode("login")}
                                         className="text-brand-violet font-medium hover:underline"
                                         data-testid="switch-to-login">Sign in</button></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
